<template>
  <form name="form" v-on:submit.prevent="submit" v-on:change="changeHandler">
    <div class="card-body">
      <vehicle-lookup
        v-on:vrm="setRegistration"
        v-on:vehicle="setVehicle"
        :stacked="false"
        label="VRN"
        :show-error="form.errors.has('registration')"
        :registration="form.registration"
      ></vehicle-lookup>

      <selectlist
        :options="types"
        v-model="form.type_id"
        label="Vehicle Type"
        name="vehicle_type"
        placeholder="Please Select"
        :show-error="form.errors.has('type')"
      ></selectlist>

      <selectlist
        :options="groups"
        v-model="form.group_id"
        label="Vehicle Group"
        label-name="name"
        placeholder="Please Select"
        :show-error="form.errors.has('group')"
        name="group"
      ></selectlist>

      <inputbox
        v-model="form.mileage"
        name="mileage"
        label="Vehicle Mileage"
        :show-error="form.errors.has('mileage')"
      ></inputbox>

      <selectlist
        :options="makes"
        v-model="form.make_id"
        label="Vehicle Make"
        placeholder="Please Select"
        :show-error="form.errors.has('make')"
        name="make"
      ></selectlist>

      <inputbox
        v-model="form.model"
        name="model"
        label="Vehicle Model"
        :show-error="form.errors.has('model')"
      ></inputbox>

      <selectlist
        :options="transmissions"
        v-model="form.transmission_id"
        name="transmission"
        placeholder="Please Select"
        label="Vehicle Transmission"
      ></selectlist>

      <div
        class="form-group"
        :class="{ 'has-error': form.errors.has('registered_at') }"
      >
        <label for="registered_at" class="col-lg-2 control-label"
          >Registration Date:*</label
        >
        <div class="col-lg-10">
          <div class="input-group">
            <datepicker
              :disabled="disabled"
              format="dd/MM/yyyy"
              v-bind:value="form.registered_at"
              input-class="form-control"
              v-on:selected="setRegistrationDate"
              v-on:input="changeHandler"
              placeholder="dd/mm/yyyy"
            ></datepicker>
            <span
              class="text-danger"
              v-text="form.errors.get('registered_at')"
              v-if="form.errors.has('registered_at')"
            ></span>
            <div class="input-group-append">
              <span class="input-group-text">
                {{ age }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <inputbox
        v-model="form.colour"
        name="vehicle_colour"
        label="Vehicle Colour"
      ></inputbox>

      <inputbox
        v-model="form.engine_capacity"
        name="engine_capacity"
        label="Engine Capacity"
      ></inputbox>

      <input-block
        v-model="form.modifications"
        name="modifications"
        label="Vehicle Modifications"
      ></input-block>

      <div class="row">
        <div class="col-md-10 offset-md-2">
          <div class="alert alert-info">
            <p class="mb-0">
              To get the tax and mot expiry dates, please go to the following
              page:
              <a
                href="https://www.vehicleenquiry.service.gov.uk/"
                target="_blank"
                >https://www.vehicleenquiry.service.gov.uk/</a
              >
            </p>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label for="tax_expiry" class="col-lg-2 control-label"
          >Tax Expiry:</label
        >
        <div class="col-lg-10">
          <datepicker
            format="dd/MM/yyyy"
            v-bind:value="form.tax_expires_at"
            input-class="form-control"
            v-on:selected="setTaxExpiry"
            v-on:input="changeHandler"
            placeholder="dd/mm/yyyy"
          ></datepicker>
          <span
            class="text-danger"
            v-text="form.errors.get('tax_expiry')"
            v-if="form.errors.has('tax_expiry')"
          ></span>
        </div>
      </div>

      <div class="form-group">
        <label for="mot_expiry" class="col-lg-2 control-label"
          >MOT Expiry:</label
        >
        <div class="col-lg-10">
          <datepicker
            format="dd/MM/yyyy"
            v-bind:value="form.mot_expires_at"
            input-class="form-control"
            v-on:selected="setMOTExpiry"
            v-on:input="changeHandler"
            placeholder="dd/mm/yyyy"
          ></datepicker>
          <span
            class="text-danger"
            v-text="form.errors.get('mot_expiry')"
            v-if="form.errors.has('mot_expiry')"
          ></span>
        </div>
      </div>

      <selectlist
        :options="last_in_charge_types"
        v-model="form.last_in_charge_type_id"
        label="Last In charge Of Vehicle"
        name="last_in_charge_type_id"
        placeholder="Please Select"
        :show-error="form.errors.has('last_in_charge_type')"
      ></selectlist>

      <toggleswitch
        :labels="true"
        name="driveable"
        label="Is the vehicle driveable?"
        v-model="form.driveable"
      ></toggleswitch>

      <toggleswitch
        :labels="true"
        name="secure"
        label="Is the vehicle secure?"
        v-model="form.secure"
      ></toggleswitch>

      <fieldset>
        <legend>
          Taxi Details
          <small class="text-muted"
            >- If this vehicle is a taxi, we need these details too</small
          >
        </legend>
      </fieldset>

      <toggleswitch
        :labels="true"
        name="taxi"
        label="Is this vehicle a taxi?"
        v-model="form.taxi"
      ></toggleswitch>

      <div v-if="form.taxi">
        <selectlist
          :options="licenceAuthorities"
          v-model="form.licence_authority_id"
          label="Licence Authority"
          placeholder="Please Select"
          name="licence_authority"
          :show-error="form.errors.has('licence_authority')"
          label-name="name"
        ></selectlist>

        <inputbox
          v-model="form.plate_number"
          name="plate_number"
          label="Plate Number"
          :show-error="form.errors.has('plate_number')"
        ></inputbox>

        <inputbox
          v-model="form.badge_number"
          name="badge_number"
          label="Badge Number"
          :show-error="form.errors.has('badge_number')"
        ></inputbox>

        <inputbox
          v-model="form.client_operator"
          name="client_operator"
          label="Client Operator"
          :show-error="form.errors.has('client_operator')"
        ></inputbox>

        <toggleswitch
          :labels="true"
          name="uber"
          label="Uber?"
          v-model="form.uber"
        ></toggleswitch>

        <toggleswitch
          :labels="true"
          name="approved"
          label="Approved?"
          v-model="form.approved"
        ></toggleswitch>
      </div>
    </div>
    <div class="card-footer text-right">
      <form-button :indicator="form.processing()" v-on:clicked="submit"
        >Save</form-button
      >
    </div>
  </form>
</template>

<script>
import Form from './../../../../Classes/Form';
import VehicleLookup from './../../../Generic/VehicleLookup.vue';

export default {
  components: {
    VehicleLookup
  },

  props: {
    incident: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      disabled: {
        from: new Date()
      },
      types: [],
      last_in_charge_types: [],
      groups: [],
      makes: [],
      transmissions: [],
      licenceAuthorities: [],
      client: null,
      form: new Form(
        {
          identifier: null,
          registration: null,
          type_id: null,
          group_id: null,
          mileage: null,
          make_id: null,
          model: null,
          transmission_id: null,
          registered_at: null,
          colour: null,
          engine_capacity: null,
          modifications: null,
          driveable: false,
          secure: false,
          tax_expires_at: null,
          mot_expires_at: null,
          taxi: false,
          licence_authority_id: null,
          plate_number: null,
          badge_number: null,
          client_operator: null,
          uber: false,
          approved: false,
          last_in_charge_type_id: null
        },
        false
      ),
      data_entered: false
    };
  },

  computed: {
    age() {
      if (!this.form.registered_at) {
        return 'Please select a date';
      }

      let yearsOld = window.DateManager.differenceInYears(
        new Date(),
        this.form.registered_at
      ).toString();
      let split = yearsOld.split('.');
      let ageInYears = split[0];

      let monthsCalc = '0.' + split[1];
      let ageInMonths = Math.floor(
        parseFloat(parseFloat(12) * parseFloat(monthsCalc))
      );

      return ageInYears + ' Years ' + ageInMonths + ' Months';
    }
  },

  mounted() {
    this.mount();
  },

  methods: {
    mount() {
      window.Ajax.get('/incidents/' + this.incident + '/client')
        .then(response => {
          this.client = response.data.identifier;

          window.Ajax.get('/vehicle-types')
            .then(response => {
              this.types = response.data;
            })
            .catch(response => {});

          window.Ajax.get('/last-in-charge-types')
            .then(response => {
              this.last_in_charge_types = response.data;
            })
            .catch(response => {});

          window.Ajax.get('/vehicle-groups')
            .then(response => {
              this.groups = response.data;
            })
            .catch(response => {});

          window.Ajax.get('/vehicle-makes')
            .then(response => {
              this.makes = response.data;
            })
            .catch(response => {});

          window.Ajax.get('/transmissions')
            .then(response => {
              this.transmissions = response.data;
            })
            .catch(response => {});

          window.Ajax.get('/licence-authorities')
            .then(response => {
              this.licenceAuthorities = response.data;
            })
            .catch(response => {});

          this.getClientVehicle();
        })
        .catch(error => {});
    },

    getClientVehicle() {
      window.Ajax.get(
        '/incidents/' + this.incident + '/client/' + this.client + '/vehicle'
      )
        .then(response => {
          let vehicle = response.data;
          this.form.registration = vehicle.registration;
          this.form.last_in_charge_type_id = vehicle.last_in_charge_type_id;
          this.form.group_id = vehicle.group_id;
          this.form.make_id = vehicle.make_id;
          this.form.model = vehicle.model;
          this.form.mileage = vehicle.mileage;
          this.form.registered_at = vehicle.registered_at
            ? window.DateManager.format(vehicle.registered_at)
            : null;
          this.form.colour = vehicle.colour;
          this.form.engine_capacity = vehicle.engine_capacity;
          this.form.tax_expires_at = vehicle.tax_expires_at
            ? window.DateManager.format(vehicle.tax_expires_at)
            : null;
          this.form.mot_expires_at = vehicle.mot_expires_at
            ? window.DateManager.format(vehicle.mot_expires_at, 'YYYY-MM-DD')
            : null;
          this.form.taxi = vehicle.taxi;
          this.form.licence_authority_id = vehicle.licence_authority_id;
          this.form.plate_number = vehicle.plate_number;
          this.form.badge_number = vehicle.badge_number;
          this.form.client_operator = vehicle.client_operator;
          this.form.uber = vehicle.uber;
          this.form.approved = vehicle.approved;
          this.form.type_id = vehicle.type_id;
          this.form.transmission_id = vehicle.transmission_id;
          this.form.modifications = vehicle.modifications;
          this.form.driveable = vehicle.driveable;
          this.form.secure = vehicle.secure;
        })
        .catch(error => {});
    },

    changeHandler() {
      if (!this.data_entered) {
        this.data_entered = true;
      }
      this.$emit('unsaved-data', this.data_entered);
    },

    submit() {
      window.toastify.info('Saving', "We're saving those details for you.");

      this.form
        .post(
          '/incidents/' + this.incident + '/client/' + this.client + '/vehicle'
        )
        .then(response => {
          this.$emit('updated', response.completed);

          window.toastify.success(
            'Saved',
            "We've updated those details for you."
          );
          // reset monitoring of form changes
          this.data_entered = false;
          this.$emit('unsaved-data', this.data_entered);
        })
        .catch(response => {
          window.toastify.error(
            'Error',
            'Sorry, there was an error saving the clients vehicle.'
          );
        });
    },

    setRegistration(registration) {
      this.form.registration = registration;
    },

    setVehicle(vehicle) {
      this.form.transmission_id = vehicle.transmission_type_id;
      this.form.make_id = vehicle.make_id;
      this.form.model = vehicle.model;
      this.form.registered_at = window.DateManager.format(
        vehicle.registered_at
      );
      this.form.colour = vehicle.colour;
      this.form.engine_capacity = vehicle.engine_capacity;

      if (vehicle.tax_expires_at) {
        this.form.tax_expires_at = window.DateManager.format(
          vehicle.tax_expires_at
        );
      }

      if (vehicle.mot_expires_at) {
        this.form.mot_expires_at = window.DateManager.format(
          vehicle.mot_expires_at
        );
      }
    },

    setRegistrationDate(value) {
      this.form.registered_at = value;
    },

    setTaxExpiry(value) {
      this.form.tax_expires_at = value;
    },

    setMOTExpiry(value) {
      this.form.mot_expires_at = value;
    }
  },

  watch: {
    $route(to, from) {
      this.mount();
    }
  }
};
</script>
